export type CampaignLaunchCheckId = 'sku' | 'icp' | 'notes' | 'active' | 'lead' | 'deal';
export type CampaignFollowOnId = 'sequence' | 'task' | 'website' | 'quote';
export type CampaignReadinessCheckId = CampaignLaunchCheckId | CampaignFollowOnId;

export type CampaignReadinessAction =
  | 'settings'
  | 'purpose'
  | 'activate'
  | 'leads'
  | 'generate'
  | 'pipeline'
  | 'sequences'
  | 'tasks'
  | 'website'
  | 'quotes';

export type CampaignReadinessItem = {
  id: CampaignReadinessCheckId;
  done: boolean;
  action: CampaignReadinessAction;
  kind: 'launch' | 'follow_on';
};

export const CAMPAIGN_NEXT_STEP_LIMITS = [3, 5] as const;
export type CampaignNextStepLimit = (typeof CAMPAIGN_NEXT_STEP_LIMITS)[number];
export const DEFAULT_CAMPAIGN_NEXT_STEP_LIMIT: CampaignNextStepLimit = 3;
export const CAMPAIGN_NEXT_STEP_LIMIT_STORAGE_KEY = 'pursuit.nextStepLimit';
export const CAMPAIGN_SELECTED_STORAGE_KEY = 'pursuit.campaignId';

export type CampaignReadinessInput = {
  channel: string;
  status: string;
  notes?: string | null;
      audience?: {
        product?: string | null;
        description?: string | null;
        criteria?: Array<{ dimension: string; value: string }>;
        matrix?: Array<{ key?: string; value?: string }>;
      } | null;
  leadCount: number;
  dealCount: number;
  /** Sequence enrollments — not catalog rows. Seed already has Discovery follow-up. */
  sequenceCount?: number;
  openTaskCount?: number;
  websiteLeadCount?: number;
  quoteCount?: number;
  nextLimit?: CampaignNextStepLimit;
};

export type CampaignReadiness = {
  percent: number;
  done: number;
  total: number;
  items: CampaignReadinessItem[];
  next: CampaignReadinessItem[];
  openLaunch: number;
  openFollowOn: number;
};

/** Earlier checklist items this step needs. Missing ids are ignored for inbound campaigns. */
export const CAMPAIGN_STEP_DEPENDS: Record<CampaignReadinessCheckId, CampaignReadinessCheckId[]> = {
  sku: [],
  icp: ['sku'],
  notes: [],
  active: ['sku', 'icp', 'notes'],
  lead: ['sku', 'icp'],
  deal: ['lead'],
  sequence: ['lead'],
  task: [],
  website: [],
  quote: ['deal'],
};

export type CampaignStepDependency = {
  id: CampaignReadinessCheckId;
  done: boolean;
};

export const campaignStepDependencies = (
  id: CampaignReadinessCheckId,
  items: CampaignReadinessItem[],
): CampaignStepDependency[] => {
  const byId = new Map(items.map((row) => [row.id, row]));
  return CAMPAIGN_STEP_DEPENDS[id]
    .map((dep) => byId.get(dep))
    .filter((row): row is CampaignReadinessItem => Boolean(row))
    .map((row) => ({ id: row.id, done: row.done }));
};

export const parseCampaignNextStepLimit = (value: unknown): CampaignNextStepLimit =>
  value === 5 || value === '5' ? 5 : DEFAULT_CAMPAIGN_NEXT_STEP_LIMIT;

/** Open checklist items, in order, filling a window of `limit`. A finished step drops off and the next open one takes its place. */
export const pickCampaignNextSteps = (
  items: CampaignReadinessItem[],
  limit: CampaignNextStepLimit = DEFAULT_CAMPAIGN_NEXT_STEP_LIMIT,
): CampaignReadinessItem[] => items.filter((row) => !row.done).slice(0, limit);

export const countCampaignLeads = (
  leads: Array<{ tags?: string | null }>,
  product?: string | null,
): number => {
  const plan = String(product || '').trim();
  if (!plan) return leads.length;
  return leads.filter((row) => String(row.tags || '') === plan).length;
};

export const countCampaignDeals = (
  opportunities: Array<{ product?: string | null }>,
  product?: string | null,
): number => {
  const plan = String(product || '').trim();
  if (!plan) return opportunities.length;
  return opportunities.filter((row) => String(row.product || '') === plan).length;
};

const followOnItems = (input: CampaignReadinessInput): CampaignReadinessItem[] => {
  const inbound = input.channel === 'website';
  const rows: CampaignReadinessItem[] = [
    {
      id: 'sequence',
      done: Number(input.sequenceCount || 0) > 0,
      action: 'sequences',
      kind: 'follow_on',
    },
    {
      id: 'task',
      done: Number(input.openTaskCount || 0) > 0,
      action: 'tasks',
      kind: 'follow_on',
    },
  ];
  if (inbound) {
    rows.push({
      id: 'website',
      done: Number(input.websiteLeadCount || 0) > 0,
      action: 'website',
      kind: 'follow_on',
    });
  }
  rows.push({
    id: 'quote',
    done: Number(input.quoteCount || 0) > 0,
    action: 'quotes',
    kind: 'follow_on',
  });
  return rows;
};

export const assessCampaignReadiness = (input: CampaignReadinessInput): CampaignReadiness => {
  const outbound = input.channel !== 'website';
  const product = String(input.audience?.product || '').trim();
  const description = String(input.audience?.description || '').trim();
  const criteria = (input.audience?.criteria || []).filter((row) => row.dimension && row.value);
  const matrix = (input.audience?.matrix || []).filter((row) => String(row.value || '').trim());
  const launch: CampaignReadinessItem[] = [];
  if (outbound) {
    launch.push({ id: 'sku', done: Boolean(product), action: 'settings', kind: 'launch' });
    launch.push({
      id: 'icp',
      done: Boolean(description) || criteria.length > 0 || matrix.length > 0,
      action: 'leads',
      kind: 'launch',
    });
  }
  launch.push({ id: 'notes', done: Boolean(String(input.notes || '').trim()), action: 'purpose', kind: 'launch' });
  launch.push({ id: 'active', done: input.status === 'active', action: 'activate', kind: 'launch' });
  launch.push({
    id: 'lead',
    done: input.leadCount > 0,
    action: 'leads',
    kind: 'launch',
  });
  launch.push({ id: 'deal', done: input.dealCount > 0, action: 'pipeline', kind: 'launch' });
  const followOn = followOnItems(input);
  const items = [...launch, ...followOn];
  const done = launch.filter((row) => row.done).length;
  const total = launch.length;
  const nextLimit = parseCampaignNextStepLimit(input.nextLimit);
  const openLaunch = launch.filter((row) => !row.done).length;
  const openFollowOn = followOn.filter((row) => !row.done).length;
  return {
    percent: total ? Math.round((done / total) * 100) : 0,
    done,
    total,
    items,
    next: pickCampaignNextSteps(items, nextLimit),
    openLaunch,
    openFollowOn,
  };
};

export type CampaignLiveReadinessInput = {
  channel: string;
  status: string;
  notes?: string | null;
  audience?: CampaignReadinessInput['audience'];
  leads: Array<{ tags?: string | null }>;
  opportunities: Array<{ product?: string | null }>;
  sequenceCount?: number;
  openTaskCount?: number;
  websiteLeadCount?: number;
  quoteCount?: number;
  nextLimit?: CampaignNextStepLimit;
  campaignLeadCount?: number;
};

/** Reassess launch next steps from the campaign row plus current leads and deals. */
export const assessCampaignReadinessFromLive = (input: CampaignLiveReadinessInput): CampaignReadiness => {
  const product = input.audience?.product;
  const leadCount =
    typeof input.campaignLeadCount === 'number' ? input.campaignLeadCount : countCampaignLeads(input.leads, product);
  return assessCampaignReadiness({
    channel: input.channel,
    status: input.status,
    notes: input.notes,
    audience: input.audience,
    leadCount,
    dealCount: countCampaignDeals(input.opportunities, product),
    sequenceCount: input.sequenceCount,
    openTaskCount: input.openTaskCount,
    websiteLeadCount: input.websiteLeadCount,
    quoteCount: input.quoteCount,
    nextLimit: input.nextLimit,
  });
};
